import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  CreateRecipeDraftDocument,
  GetRecipeDocument,
  type RecipeDetailFragment,
} from "../../gql/generated/graphqlSchema.tsx";
import { NotFoundWithBack } from "../../components/NotFoundWithBack.tsx";
import { PageLoader } from "../../components/PageLoader.tsx";
import { useSafeParams } from "../../hooks/useSafeParams.ts";
import { RecipeProvider } from "./RecipeContext.tsx";
import { RecipeDetailEdit } from "./edit/RecipeDetailEdit.tsx";
import { RecipeDetailView } from "./view/RecipeDetailView.tsx";
import { RefreshProvider, useUpdateRefreshKey } from "../../context/RefreshContext.tsx";

const paramsSchema = z.object({ id: z.string().min(1) });

type DetailInnerProps = {
  readonly mode: "view" | "edit";
};

const RecipeDetailPageInner = ({ mode }: DetailInnerProps) => {
  const { t } = useTranslation();
  const { id } = useSafeParams(paramsSchema);
  const { data, loading, refetch } = useQuery(GetRecipeDocument, { variables: { id } });
  const [previousData, setPreviousData] = useState(data);
  const [recipe, setRecipe] = useState<RecipeDetailFragment | null>(null);

  const [createDraft, { loading: creatingDraft }] = useMutation(CreateRecipeDraftDocument);

  if (data !== previousData) {
    setPreviousData(data);
    if (mode === "edit") {
      setRecipe(data?.draftRecipe ?? null);
    } else {
      setRecipe(data?.publishedRecipe ?? null);
    }
  }
  const updateRefreshKey = useUpdateRefreshKey();

  const setRecipeWithRefreshKeyUpdate = useCallback(
    (newRecipe: RecipeDetailFragment | undefined | null) => {
      if (newRecipe) {
        setRecipe(newRecipe);
        updateRefreshKey();
      }
    },
    [updateRefreshKey],
  );

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  useEffect(() => {
    if (mode !== "edit" || loading) return;
    if (data && !data.draftRecipe && data.publishedRecipe && !data.publishedRecipe.deleted) {
      void createDraft({ variables: { id } }).then(() => void refetch());
    }
  }, [mode, loading, data, id, createDraft, refetch]);

  if (loading || creatingDraft) {
    return <PageLoader />;
  }

  if (!recipe) {
    return <NotFoundWithBack description={t("recipes.notFound")} />;
  }

  return (
    <RecipeProvider recipe={recipe} refetch={handleRefetch} setRecipe={setRecipeWithRefreshKeyUpdate}>
      {mode === "edit" && !recipe.deleted ? (
        <RecipeDetailEdit key={recipe.versionId} />
      ) : (
        <RecipeDetailView key={recipe.versionId} />
      )}
    </RecipeProvider>
  );
};

// eslint-disable-next-line react/no-multi-comp
export const RecipeDetailPage = () => (
  <RefreshProvider>
    <RecipeDetailPageInner mode="view" />
  </RefreshProvider>
);

// eslint-disable-next-line react/no-multi-comp
export const RecipeDetailEditPage = () => (
  <RefreshProvider>
    <RecipeDetailPageInner mode="edit" />
  </RefreshProvider>
);
